import { useMemo, useRef, useState } from 'react';

import { Calendar } from 'primereact/calendar';
import { Nullable } from 'primereact/ts-helpers';
import { format, differenceInCalendarDays, isWithinInterval, isSameDay } from 'date-fns';

import OButton from '@src/shared/OButton';

import styles from './OCustomCalendar.module.scss';

interface IOCustomCalendarProps {
  value: Date[] | null;
  onChange: (value: Date[] | null) => void;
  onClose?: () => void;
  onApply?: () => void;
  dateFormat?: string;
  minDate?: Date;
  isMobile?: boolean;
}

const OCustomCalendar = ({
  value,
  onChange,
  onClose,
  onApply,
  dateFormat = 'EEE, dd MMM, yyyy',
  minDate,
  isMobile = false,
}: IOCustomCalendarProps) => {
  const calendarRef = useRef<any>(null);
  const [visibleDate, setVisibleDate] = useState(new Date());
  const [draftValue, setDraftValue] = useState<Date[] | null>(value);

  const dateTemplate = (dateMeta: {
    day: number;
    month: number;
    year: number;
    otherMonth: boolean;
    today: boolean;
    selectable: boolean;
  }) => {
    const cellDate = new Date(dateMeta.year, dateMeta.month, dateMeta.day);

    const classes: string[] = [];
    let isMiddle = false;
    let isFirst = false;
    let isLast = false;

    if (draftValue?.length === 2) {
      const [start, end] = draftValue;
      const inRange = isWithinInterval(cellDate, { start, end });

      if (inRange) {
        // everyone in the range gets Prime’s highlight class
        classes.push('p-highlight');

        // first day gets your left-cap
        if (isSameDay(cellDate, start)) {
          isFirst = true;
        }
        // last day gets your right-cap
        else if (isSameDay(cellDate, end)) {
          isLast = true;
        }
        // middle days: reset any border radius
        else {
          isMiddle = true;
        }
      }
    }

    return (
      <span className={classes.join(' ')} is-middle={`${isMiddle}`} is-first={`${isFirst}`} is-last={`${isLast}`}>
        {dateMeta.day}
      </span>
    );
  };

  // compute how many days are selected (inclusive)
  const selectedDaysCount = useMemo(() => {
    if (draftValue?.[0] && draftValue?.[1]) {
      // +1 to include both endpoints
      return differenceInCalendarDays(draftValue[1], draftValue[0]) + 1;
    } else if (draftValue?.length === 1 && draftValue[0]) {
      return 1;
    }
    return 0;
  }, [draftValue]);

  const handleDateChange = (event: { value: Nullable<(Date | null)[]> }) => {
    const value = event.value;
    if (value) {
      const validDates = value.filter((date): date is Date => date !== null);
      setDraftValue(validDates);
    }
  };

  const handleCancel = () => {
    // discard changes
    setDraftValue(value);
    onClose?.();
  };

  const handleApply = () => {
    // commit staged dates
    onChange(draftValue);
    onApply?.();
  };

  const footerTemplate = (): React.ReactNode => {
    return (
      <div className={styles.footerWrapper}>
        {draftValue && (
          <div className={styles.selectionInfo}>
            <span className={styles.countText}>
              <strong>
                {selectedDaysCount} day
                {selectedDaysCount > 1 ? 's' : ''}
              </strong>{' '}
              selected
            </span>
            <button
              type="button"
              className={styles.clearButton}
              onClick={() => setDraftValue(null)}
              aria-label="Clear selection"
            >
              ✕
            </button>
          </div>
        )}

        {/* bottom row: Cancel / Apply */}
        <div className={styles.actions}>
          <OButton outlined onClick={handleCancel} text="Cancel" variant="secondary" size="m-small" />
          <OButton onClick={handleApply} text="Apply" variant="primary" size="m-small" />
        </div>
      </div>
    );
  };

  const footerTemplateMobile = (): React.ReactNode => {
    return (
      <div className={styles.footerWrapperMobile}>
        {draftValue && (
          <div className={styles.selectionInfo}>
            <span className={styles.countText}>
              <strong>
                {selectedDaysCount} day
                {selectedDaysCount > 1 ? 's' : ''}
              </strong>{' '}
              selected
            </span>
          </div>
        )}
        <div className={styles.actions}>
          <OButton onClick={handleApply} text="Apply" variant="primary" size="full" />
        </div>
      </div>
    );
  };

  return (
    <div>
      <Calendar
        inline
        ref={calendarRef}
        className={styles.customCalendar}
        value={draftValue}
        onChange={handleDateChange}
        onViewDateChange={(e) => setVisibleDate(e.value)}
        viewDate={visibleDate}
        showTime={false}
        minDate={minDate}
        selectionMode="range"
        numberOfMonths={isMobile ? 1 : 2}
        formatDateTime={(date) => format(date, dateFormat)}
        footerTemplate={isMobile ? footerTemplateMobile : footerTemplate}
        dateTemplate={dateTemplate}
      />
    </div>
  );
};

export default OCustomCalendar;
